import styled from "@emotion/styled";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ActionMenu,
  ActionMenuItem,
  Button,
  IActionMenuOption,
  Info,
  Tooltip,
} from "@livechat/design-system-react-components";

import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useToggle } from "hooks/use-toggle";
import { TicketSilo } from "interfaces/entities/ticket";
import { selectLoggedInAgent, selectLoggedInAgentPermission } from "store/entities/agents";
import { CustomViewAuthor } from "store/entities/custom-views/interfaces";
import { selectCustomViewsAlphabetically } from "store/entities/custom-views/selectors";
import { SessionActions, selectTransientFlag } from "store/features/session";
import { TicketsViewActions } from "store/views/tickets/actions";
import { getSilo, selectActiveCustomView } from "store/views/tickets/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";

enum Option {
  MODIFY,
  NEW,
}

interface IProps {
  onCreateCustomView: () => void;
}

export const SaveCustomView: FC<IProps> = ({ onCreateCustomView }: IProps) => {
  const activeCustomView = useSelector(selectActiveCustomView);
  const isAdmin = useSelector(selectLoggedInAgentPermission) === Role.Owner;
  const silo = useSelector(getSilo);
  const [tooltipShown, toggleTooltip] = useState(false);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const tooltipAlreadyShown = useSelector(selectTransientFlag("customViewTooltipToggled"));
  const customViews = useSelector(selectCustomViewsAlphabetically);
  const currentAgent = useSelector(selectLoggedInAgent);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!tooltipAlreadyShown && !tooltipShown) {
      const hasAuthoredCustomView = customViews.find(
        view => view.createdByType === CustomViewAuthor.Agent && view.createdBy === currentAgent.id
      );

      if (!hasAuthoredCustomView) {
        dispatch(SessionActions.setTransientFlag("customViewTooltipToggled", true));
        toggleTooltip(true);
        trackEngagement("Save view: Tooltip shown", EventPlace.Tickets);
      }
    }
  }, [dispatch, customViews, tooltipAlreadyShown, tooltipShown, currentAgent.id]);

  const onItemSelect = useCallback(
    (id: number) => {
      if (id === Option.MODIFY) {
        dispatch(TicketsViewActions.updateCustomView());
      } else {
        onCreateCustomView();
      }
      toggleIsOpen();
    },
    [onCreateCustomView, toggleIsOpen]
  );

  const hideTooltip = useCallback(() => {
    toggleTooltip(false);
    trackEngagement("Save view: Tooltip closed by user", EventPlace.Tickets);
  }, []);

  const items: IActionMenuOption[] = useMemo(
    () => [
      ...(isAdmin || activeCustomView?.agentID !== null
        ? [
            {
              key: Option.MODIFY.toString(),
              element: <ActionMenuItem>Update view</ActionMenuItem>,
              onClick: () => onItemSelect(Option.MODIFY),
            },
          ]
        : []),
      {
        key: Option.NEW.toString(),
        element: <ActionMenuItem>Save as new view…</ActionMenuItem>,
        onClick: () => onItemSelect(Option.NEW),
      },
    ],
    [onItemSelect]
  );

  if (silo !== TicketSilo.Tickets) {
    return null;
  }

  if (!activeCustomView) {
    return (
      <Tooltip
        triggerRenderer={() => (
          <SubmitViewButton kind="secondary" size="compact" onClick={onCreateCustomView}>
            Create view
          </SubmitViewButton>
        )}
        placement="bottom"
        isVisible={tooltipShown}
        css={{ zIndex: StackingContextLevel.AppHeader }}
      >
        <Info
          header="Save filters as views"
          text="Choose a set of filters and save them as a view. Use this view to get the most important tickets even
            faster."
          closeWithX
          handleCloseAction={hideTooltip}
        />
      </Tooltip>
    );
  }

  return (
    <ActionMenu
      visible={isOpen}
      placement="top-start"
      options={items}
      triggerRenderer={
        <SubmitViewButton
          kind="secondary"
          size="compact"
          onClick={toggleIsOpen}
          data-testid="update-custom-view-button"
        >
          Update view
        </SubmitViewButton>
      }
    />
  );
};

// TODO: Remove this styled component and use the Button component from the design system when DS fix for min width for buttons is released
const SubmitViewButton = styled(Button)`
  min-width: 116px;
`;
